// src/SolarCalculator.js
import React, { useState } from 'react';
import '../css/SolarCalculator.css';
import SolarRevenueCalculatorLogo from '../assets/SolarRevenueCalculator-main.png';
import HeroImage from '../assets/HeroImage.png';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import Tooltip from './Tooltip';
import ResultsPage from './ResultsPage';
import CalculationExplanation from './CalculationExplanation';
import { Link } from 'react-router-dom';

const SolarCalculator = () => {
  // State variables for inputs
  const [monthlyUsage, setMonthlyUsage] = useState('');
  const [panelWattage, setPanelWattage] = useState(300);
  const [sunHours, setSunHours] = useState(5);
  const [systemLosses, setSystemLosses] = useState(15);
  const [inverterEfficiency, setInverterEfficiency] = useState(95);
  const [panelCostPerWatt, setPanelCostPerWatt] = useState(1.5); // Cost of panels per watt
  const [installationCostPerWatt, setInstallationCostPerWatt] = useState(1.0); // Cost of installation per watt
  const [incentives, setIncentives] = useState(0); // Default value set to 0 pounds

  // New state variables
  const [sizeOfArea, setSizeOfArea] = useState('');
  const [roofPitch, setRoofPitch] = useState(25);
  const [orientation, setOrientation] = useState('');
  const [shading, setShading] = useState('');
  const [homeOccupancy, setHomeOccupancy] = useState('');
  const [heatingSource, setHeatingSource] = useState('');
  const [usePlugInHeater, setUsePlugInHeater] = useState(false);
  const [useElectricWaterHeater, setUseElectricWaterHeater] = useState(false);
  const [haveElectricVehicle, setHaveElectricVehicle] = useState(false);
  const [haveHighDemandAppliance, setHaveHighDemandAppliance] = useState(false);
  const [selectedRegion, setSelectedRegion] = useState('');

  // Add a new state variable for toggling advanced options
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);

  // State variables for results
  const [dailyUsage, setDailyUsage] = useState(null);
  const [systemSize, setSystemSize] = useState(null);
  const [numberOfPanels, setNumberOfPanels] = useState(null);
  const [totalPanelCost, setTotalPanelCost] = useState(null); // New state
  const [totalInstallationCost, setTotalInstallationCost] = useState(null); // New state
  const [totalCost, setTotalCost] = useState(null);
  const [netCost, setNetCost] = useState(null);
  const [adjustedEfficiency, setAdjustedEfficiency] = useState(null);
  const [powerReceived, setPowerReceived] = useState(null);
  const [currentAnnualCost, setCurrentAnnualCost] = useState(null);
  const [annualSolarProduction, setAnnualSolarProduction] = useState(null);
  const [annualSavings, setAnnualSavings] = useState(null);
  const [paybackTime, setPaybackTime] = useState(null);
  const [exportProfit, setExportProfit] = useState(null); // New state for export profit

  // New state variables
  const [exportRate, setExportRate] = useState(16.5); // New state variable for export rate (pence per kWh)
  const [percentageExported, setPercentageExported] = useState(50); // New state variable for percentage exported (%)

  // Electricity Rates JSON Data
  const electricityRates = [
    {
      region: "Eastern",
      electricityUnitRatePerKWh: "25.21p",
      electricityDailyStandingCharge: "50.84p"
    },
    {
      region: "East Midlands",
      electricityUnitRatePerKWh: "23.77p",
      electricityDailyStandingCharge: "56.90p"
    },
    {
      region: "London",
      electricityUnitRatePerKWh: "25.69p",
      electricityDailyStandingCharge: "41.59p"
    },
    {
      region: "Midlands",
      electricityUnitRatePerKWh: "23.83p",
      electricityDailyStandingCharge: "63.62p"
    },
    {
      region: "Northern",
      electricityUnitRatePerKWh: "23.19p",
      electricityDailyStandingCharge: "72.10p"
    },
    {
      region: "Northern Scotland",
      electricityUnitRatePerKWh: "24.96p",
      electricityDailyStandingCharge: "61.98p"
    },
    {
      region: "North West",
      electricityUnitRatePerKWh: "25.01p",
      electricityDailyStandingCharge: "52.04p"
    },
    {
      region: "North Wales & Mersey",
      electricityUnitRatePerKWh: "25.39p",
      electricityDailyStandingCharge: "67.89p"
    },
    {
      region: "Southern",
      electricityUnitRatePerKWh: "24.62p",
      electricityDailyStandingCharge: "64.28p"
    },
    {
      region: "South East",
      electricityUnitRatePerKWh: "25.24p",
      electricityDailyStandingCharge: "57.84p"
    },
    {
      region: "Southern Scotland",
      electricityUnitRatePerKWh: "23.96p",
      electricityDailyStandingCharge: "64.17p"
    },
    {
      region: "South Wales",
      electricityUnitRatePerKWh: "24.49p",
      electricityDailyStandingCharge: "64.12p"
    },
    {
      region: "Southern Western",
      electricityUnitRatePerKWh: "24.18p",
      electricityDailyStandingCharge: "68.12p"
    },
    {
      region: "Yorkshire",
      electricityUnitRatePerKWh: "23.51p",
      electricityDailyStandingCharge: "68.32p"
    }
  ];

  // Average values for the UK
  const AVERAGE_LATITUDE = 54.0; // degrees North
  const AVERAGE_LONGITUDE = -2.0; // degrees East
  const AVERAGE_IRRADIANCE = 1000; // kWh/m²/year

  const tooltips = {
    monthlyUsage: "This is the amount of electricity your home consumes on average each month, measured in kilowatt-hours (kWh). You can find this information on your electricity bills. Add up the kWh used over a few months (ideally 12 months for accuracy) and divide by the number of months to get an average.",
    panelWattage: "This is the power output rating of each individual solar panel, typically ranging from 250W to 400W. Higher wattage panels generate more electricity, meaning you may need fewer panels to meet your energy needs. Check the specifications of the solar panels you're considering.",
    sunHours: "This is the average number of peak sunlight hours your location receives each day. In the UK, this varies by region. Southern England might get around 4-5 hours per day, while northern areas might receive around 3-4 hours. Check UK-specific solar insolation maps for more precise figures.",
    systemLosses: "This is the percentage of energy lost due to factors such as shading, dirt, temperature variations, and wiring. System losses typically range between 10-20%. A good general estimate is around 15% losses, but this may be higher if you have more shading.",
    inverterEfficiency: "Inverter efficiency refers to how well the inverter converts DC electricity from your solar panels into AC electricity for your home. Most modern inverters used in the UK have an efficiency between 90-98%. Check the technical specifications of the inverter you're considering.",
    panelCostPerWatt: "Enter the cost per watt for the solar panels alone.",
    installationCostPerWatt: "Enter the cost per watt for installing the solar panels.",
    incentives: "Enter the total amount of available incentives or tax credits in pounds. This is the absolute value that will be deducted from the total cost of your solar panel system.",
    exportRate: "Enter the rate (in pence per kWh) at which your energy supplier pays you for electricity exported back to the grid. Based on current UK rates, this typically ranges between 6p and 30p per kWh.",
    percentageExported: "Enter the percentage of generated electricity that you expect to export back to the grid. For most households without an export meter, this is typically assumed to be 50%.",
  };

  const getRoofPitchDescription = (pitch) => {
    if (pitch <= 5) return "Flat";
    if (pitch < 25) return "Shallow";
    if (pitch >= 25 && pitch <= 47.5) return "Conventional";
    if (pitch > 47.5) return "High";
    return "Invalid";
  };

  const degreeToDirection = (degree) => {
    if (degree === 0) return "North";
    if (degree === 45) return "Northeast";
    if (degree === 90) return "East";
    if (degree === 135) return "Southeast";
    if (degree === 180) return "South";
    if (degree === 225) return "Southwest";
    if (degree === 270) return "West";
    if (degree === 315) return "Northwest";
    return "Unknown";
  };

  // Utility functions for solar calculations
  const degToRad = (degrees) => degrees * (Math.PI / 180);
  const radToDeg = (radians) => radians * (180 / Math.PI);

  /**
   * Calculate the optimal tilt angle based on latitude
   * Simplified for average annual optimization
   */
  const calculateOptimalTilt = (latitude) => {
    return latitude * 0.76 + 3.1; // Using a heuristic formula
  };

  /**
   * Calculate the adjustment factor based on tilt and orientation
   */
  const calculateTiltAdjustment = (roofPitch, orientation) => {
    // Convert orientation to the difference from South (for Northern Hemisphere)
    // 180° is South
    let orientationDifference = Math.abs(orientation - 180);
    if (orientationDifference > 180) {
      orientationDifference = 360 - orientationDifference;
    }

    // Simple adjustment: max efficiency when facing South, decreases as it deviates
    const orientationFactor = Math.cos(degToRad(orientationDifference));

    // Tilt factor: optimal tilt based on latitude vs actual tilt
    const optimalTilt = calculateOptimalTilt(AVERAGE_LATITUDE);
    const tiltDifference = Math.abs(optimalTilt - roofPitch);
    // Efficiency decreases as tilt difference increases
    const tiltFactor = Math.cos(degToRad(tiltDifference));

    // Overall adjustment factor
    const adjustmentFactor = (orientationFactor + tiltFactor) / 2; // Average the two factors

    return adjustmentFactor; // Between -1 and 1, but realistically between 0 and 1
  };

  /**
   * Calculate the annual energy output adjustment based on tilt and orientation
   */
  const calculateAnnualAdjustment = (roofPitch, orientation) => {
    const adjustmentFactor = calculateTiltAdjustment(roofPitch, orientation);
    return adjustmentFactor;
  };

  /**
   * Shading adjustment factors
   */
  const shadingAdjustmentMap = {
    none: 1.0,         // No reduction
    some: 0.9,         // 10% reduction
    significant: 0.8,  // 20% reduction
    heavy: 0.7         // 30% reduction
  };

  // Handler for form submission
  const handleSubmit = (e) => {
    e.preventDefault();

    // Validate inputs
    if (
      isNaN(monthlyUsage) ||
      isNaN(panelWattage) ||
      isNaN(sunHours) ||
      isNaN(systemLosses) ||
      isNaN(inverterEfficiency) ||
      isNaN(panelCostPerWatt) ||
      isNaN(installationCostPerWatt) ||
      isNaN(incentives) ||
      isNaN(roofPitch) ||
      isNaN(orientation) ||
      shading === '' ||
      selectedRegion === '' ||
      isNaN(exportRate) ||
      isNaN(percentageExported)
    ) {
      alert('Please enter valid numbers in all fields, select shading level, and select a region.');
      return;
    }

    // Find the selected region's electricity rates
    const regionData = electricityRates.find(region => region.region === selectedRegion);
    if (!regionData) {
      alert('Selected region not found.');
      return;
    }

    // Parse the electricity rates (remove 'p' and convert to pounds)
    const unitRatePence = parseFloat(regionData.electricityUnitRatePerKWh.replace('p', ''));
    const dailyStandingChargePence = parseFloat(regionData.electricityDailyStandingCharge.replace('p', ''));

    const unitRate = unitRatePence / 100; // Convert pence to pounds
    const dailyStandingCharge = dailyStandingChargePence / 100; // Convert pence to pounds

    // 1. Calculate Average Daily Electricity Usage
    const daily = parseFloat(monthlyUsage) / 30;

    // 2. Calculate System Efficiency
    const baseEfficiency = (parseFloat(inverterEfficiency) / 100) * (1 - parseFloat(systemLosses) / 100);

    // 3. Calculate System Size (kW)
    const sizeKW = daily / (parseFloat(sunHours) * baseEfficiency);

    // 4. Calculate Number of Panels
    const sizeW = sizeKW * 1000;
    const panels = Math.ceil(sizeW / parseFloat(panelWattage));

    // 5. Calculate Total Panel Cost
    const totalPanelCostCalculated = sizeW * parseFloat(panelCostPerWatt);

    // 6. Calculate Total Installation Cost
    const totalInstallationCostCalculated = sizeW * parseFloat(installationCostPerWatt);

    // 7. Calculate Total Cost
    const total = totalPanelCostCalculated + totalInstallationCostCalculated;

    // 8. Calculate Net Cost after Incentives
    const net = total - parseFloat(incentives); // Subtract the absolute incentive value

    // 9. Calculate Tilt and Orientation Adjustment
    const tiltOrientationFactor = calculateAnnualAdjustment(parseFloat(roofPitch), parseFloat(orientation));

    // 10. Get Shading Adjustment Factor
    const shadingFactor = shadingAdjustmentMap[shading] || 1.0;

    // 11. Calculate Overall Adjustment Factor
    const overallAdjustmentFactor = tiltOrientationFactor * shadingFactor;

    // 12. Calculate Adjusted Efficiency
    const adjustedEff = baseEfficiency * overallAdjustmentFactor;

    // 13. Calculate Power Received
    const powerReceivedPerPanel = (parseFloat(panelWattage) * parseFloat(sunHours) * adjustedEff) / 1000; // kWh/day per panel
    const totalPowerReceived = powerReceivedPerPanel * panels; // Total kWh/day

    // 14. Calculate Annual Solar Production
    const annualSolarProduction = totalPowerReceived * 365; // kWh/year

    // 15. Calculate Current Annual Electricity Cost
    const annualUsage = daily * 365; // kWh/year
    const currentAnnualCost = (annualUsage * unitRate) + (dailyStandingCharge * 365); // Pounds

    // 16. Calculate Annual Savings
    const selfConsumptionPercentage = 100 - parseFloat(percentageExported);
    const annualSavings = (annualSolarProduction * (selfConsumptionPercentage / 100)) * unitRate; // Pounds

    // 17. Calculate Export Profit using user inputs
    const exportRatePounds = parseFloat(exportRate) / 100; // Convert pence to pounds
    const exportedEnergy = annualSolarProduction * (parseFloat(percentageExported) / 100); // kWh/year
    const exportProfitCalculated = exportedEnergy * exportRatePounds; // Pounds

    // 18. Calculate Payback Time
    const paybackTimeCalculated = net / (annualSavings + exportProfitCalculated); // Years

    // Create results object
    const results = {
      dailyUsage: daily.toFixed(2),
      systemSize: sizeKW.toFixed(2),
      numberOfPanels: panels,
      totalPanelCost: totalPanelCostCalculated.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
      totalInstallationCost: totalInstallationCostCalculated.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
      totalCost: total.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
      netCost: net.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
      adjustedEfficiency: (adjustedEff * 100).toFixed(2),
      powerReceived: totalPowerReceived.toFixed(2),
      currentAnnualCost: currentAnnualCost.toFixed(2),
      annualSolarProduction: annualSolarProduction.toFixed(2),
      annualSavings: annualSavings.toFixed(2),
      exportProfit: exportProfitCalculated.toFixed(2), // New result for export profit
      paybackTime: paybackTimeCalculated.toFixed(2) // Updated payback time
    };

    // Update state with results
    setDailyUsage(results.dailyUsage);
    setSystemSize(results.systemSize);
    setNumberOfPanels(results.numberOfPanels);
    setTotalPanelCost(results.totalPanelCost);
    setTotalInstallationCost(results.totalInstallationCost);
    setTotalCost(results.totalCost);
    setNetCost(results.netCost);
    setAdjustedEfficiency(results.adjustedEfficiency);
    setPowerReceived(results.powerReceived);
    setCurrentAnnualCost(results.currentAnnualCost);
    setAnnualSolarProduction(results.annualSolarProduction);
    setAnnualSavings(results.annualSavings);
    setExportProfit(results.exportProfit);
    setPaybackTime(results.paybackTime);

    // Immediately show the results page
    setShowResults(true);
  };

  const [showResults, setShowResults] = useState(false);
  const [showExplanation, setShowExplanation] = useState(false);

  return (
    <>
      <div className="hero-banner">
        <div className="hero-content">
          <div className="hero-left">
            <img src={SolarRevenueCalculatorLogo} alt="Solar Panel" className="hero-image" />
            <h1>Solar Wealth Planner</h1>
          </div>
          <Link to="/privacy-policy" className="privacy-policy-link">Privacy Policy</Link>
        </div>
      </div>
      <div className="hero-image-container">
        <img 
            src={HeroImage} 
            alt="Solar Panel" 
            className="hero-image" 
            style={{
                width: '100%',
                height: 'auto',
                margin: '0 auto',
                position: 'absolute',
                top: '0',
                left: '0',
                zIndex: '-1'
            }}
        />
      </div>
      <div className="calculator-container">
        <div className="two-column-layout">
          <div className="form-column">
            <h2>Calculator</h2>
            <form onSubmit={handleSubmit} className="calculator-form">
              <div className="form-columns">
                <div className="form-column">
                  <div className="form-group">
                    <label htmlFor="monthlyUsage">
                      Average Monthly Electricity Usage (kWh):
                      <Tooltip content={tooltips.monthlyUsage}>
                        <span className="info-icon">
                          <FontAwesomeIcon icon={faInfoCircle} />
                        </span>
                      </Tooltip>
                    </label>
                    <input
                      type="number"
                      id="monthlyUsage"
                      value={monthlyUsage}
                      onChange={(e) => setMonthlyUsage(e.target.value)}
                      required
                      min="0"
                      step="any"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="region">
                      Region:
                      <Tooltip content="Select your region to apply the appropriate electricity rates">
                        <span className="info-icon">
                          <FontAwesomeIcon icon={faInfoCircle} />
                        </span>
                      </Tooltip>
                    </label>
                    <div className="styled-select">
                      <select
                        id="region"
                        value={selectedRegion}
                        onChange={(e) => setSelectedRegion(e.target.value)}
                        required
                      >
                        <option value="">Select region</option>
                        {electricityRates.map((region) => (
                          <option key={region.region} value={region.region}>
                            {region.region}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="form-group">
                    <label htmlFor="sizeOfArea">
                      Size of Area:
                      <Tooltip content="Select the approximate size of your roof or installation area">
                        <span className="info-icon">
                          <FontAwesomeIcon icon={faInfoCircle} />
                        </span>
                      </Tooltip>
                    </label>
                    <div className="styled-select">
                      <select
                        id="sizeOfArea"
                        value={sizeOfArea}
                        onChange={(e) => setSizeOfArea(e.target.value)}
                        required
                      >
                        <option value="">Select size</option>
                        <option value="small">Small (up to 4 panels)</option>
                        <option value="average">Average (5 to 8 panels)</option>
                        <option value="large">Large (9 to 12 panels)</option>
                        <option value="veryLarge">Very large (13 or more panels)</option>
                      </select>
                    </div>
                  </div>

                  <div className="form-group">
                    <label htmlFor="roofPitch">
                      Roof Pitch: {roofPitch}°
                      <Tooltip content="Adjust the slider to set the angle of your roof in degrees">
                        <span className="info-icon">
                          <FontAwesomeIcon icon={faInfoCircle} />
                        </span>
                      </Tooltip>
                    </label>
                    <input
                      type="range"
                      id="roofPitch"
                      value={roofPitch}
                      onChange={(e) => setRoofPitch(parseInt(e.target.value))}
                      min="0"
                      max="60"
                      step="1"
                    />
                    <p className="roof-pitch-description">Roof Type: {getRoofPitchDescription(roofPitch)}</p>
                  </div>

                  <div className="form-group">
                    <label htmlFor="orientation">
                      Orientation:
                      <Tooltip content="Select the direction your roof or installation area faces">
                        <span className="info-icon">
                          <FontAwesomeIcon icon={faInfoCircle} />
                        </span>
                      </Tooltip>
                    </label>
                    <div className="styled-select">
                      <select
                        id="orientation"
                        value={orientation}
                        onChange={(e) => setOrientation(parseInt(e.target.value))}
                        required
                      >
                        <option value="">Select orientation</option>
                        <option value="0">North (0°)</option>
                        <option value="45">Northeast (45°)</option>
                        <option value="90">East (90°)</option>
                        <option value="135">Southeast (135°)</option>
                        <option value="180">South (180°)</option>
                        <option value="225">Southwest (225°)</option>
                        <option value="270">West (270°)</option>
                        <option value="315">Northwest (315°)</option>
                      </select>
                    </div>
                  </div>

                  <div className="form-group">
                    <label htmlFor="shading">
                      Shading:
                      <Tooltip content="Estimate the level of shading on your installation area">
                        <span className="info-icon">
                          <FontAwesomeIcon icon={faInfoCircle} />
                        </span>
                      </Tooltip>
                    </label>
                    <div className="styled-select">
                      <select
                        id="shading"
                        value={shading}
                        onChange={(e) => setShading(e.target.value)}
                        required
                      >
                        <option value="">Select shading level</option>
                        <option value="none">None</option>
                        <option value="some">Some</option>
                        <option value="significant">Significant</option>
                        <option value="heavy">Heavy</option>
                      </select>
                    </div>
                  </div>

                  {/* Advanced options toggle */}
                  <div className="form-group">
                    <button
                      type="button"
                      onClick={() => setShowAdvancedOptions(!showAdvancedOptions)}
                      className="advanced-options-toggle"
                    >
                      {showAdvancedOptions ? 'Hide Advanced Options' : 'Show Advanced Options'}
                    </button>
                  </div>

                  {/* Advanced options section */}
                  {showAdvancedOptions && (
                    <>
                      <div className="form-group">
                        <label htmlFor="sunHours">
                          Average Daily Sun Hours (PSH):
                          <Tooltip content={tooltips.sunHours}>
                            <span className="info-icon">
                              <FontAwesomeIcon icon={faInfoCircle} />
                            </span>
                          </Tooltip>
                        </label>
                        <input
                          type="number"
                          id="sunHours"
                          value={sunHours}
                          onChange={(e) => setSunHours(e.target.value)}
                          required
                          min="0"
                          step="any"
                        />
                      </div>

                      <div className="form-group">
                        <label htmlFor="panelWattage">
                          Solar Panel Wattage (W):
                          <Tooltip content={tooltips.panelWattage}>
                            <span className="info-icon">
                              <FontAwesomeIcon icon={faInfoCircle} />
                            </span>
                          </Tooltip>
                        </label>
                        <input
                          type="number"
                          id="panelWattage"
                          value={panelWattage}
                          onChange={(e) => setPanelWattage(e.target.value)}
                          required
                          min="0"
                          step="any"
                        />
                      </div>

                      <div className="form-group">
                        <label htmlFor="systemLosses">
                          System Losses (%):
                          <Tooltip content={tooltips.systemLosses}>
                            <span className="info-icon">
                              <FontAwesomeIcon icon={faInfoCircle} />
                            </span>
                          </Tooltip>
                        </label>
                        <input
                          type="number"
                          id="systemLosses"
                          value={systemLosses}
                          onChange={(e) => setSystemLosses(e.target.value)}
                          required
                          min="0"
                          max="100"
                          step="any"
                        />
                      </div>

                      <div className="form-group">
                        <label htmlFor="inverterEfficiency">
                          Inverter Efficiency (%):
                          <Tooltip content={tooltips.inverterEfficiency}>
                            <span className="info-icon">
                              <FontAwesomeIcon icon={faInfoCircle} />
                            </span>
                          </Tooltip>
                        </label>
                        <input
                          type="number"
                          id="inverterEfficiency"
                          value={inverterEfficiency}
                          onChange={(e) => setInverterEfficiency(e.target.value)}
                          required
                          min="0"
                          max="100"
                          step="any"
                        />
                      </div>

                      <div className="form-group">
                        <label htmlFor="panelCostPerWatt">
                          Panel Cost per Watt (£/W):
                          <Tooltip content={tooltips.panelCostPerWatt}>
                            <span className="info-icon">
                              <FontAwesomeIcon icon={faInfoCircle} />
                            </span>
                          </Tooltip>
                        </label>
                        <input
                          type="number"
                          id="panelCostPerWatt"
                          value={panelCostPerWatt}
                          onChange={(e) => setPanelCostPerWatt(e.target.value)}
                          required
                          min="0"
                          step="any"
                        />
                      </div>

                      <div className="form-group">
                        <label htmlFor="installationCostPerWatt">
                          Installation Cost per Watt (£/W):
                          <Tooltip content={tooltips.installationCostPerWatt}>
                            <span className="info-icon">
                              <FontAwesomeIcon icon={faInfoCircle} />
                            </span>
                          </Tooltip>
                        </label>
                        <input
                          type="number"
                          id="installationCostPerWatt"
                          value={installationCostPerWatt}
                          onChange={(e) => setInstallationCostPerWatt(e.target.value)}
                          required
                          min="0"
                          step="any"
                        />
                      </div>

                      <div className="form-group">
                        <label htmlFor="incentives">
                          Available Incentives/Tax Credits (£):
                          <Tooltip content={tooltips.incentives}>
                            <span className="info-icon">
                              <FontAwesomeIcon icon={faInfoCircle} />
                            </span>
                          </Tooltip>
                        </label>
                        <input
                          type="number"
                          id="incentives"
                          value={incentives}
                          onChange={(e) => setIncentives(e.target.value)}
                          required
                          min="0"
                          step="any"
                        />
                      </div>

                      {/* New input for Export Rate */}
                      <div className="form-group">
                        <label htmlFor="exportRate">
                          Export Rate (p/kWh):
                          <Tooltip content={tooltips.exportRate}>
                            <span className="info-icon">
                              <FontAwesomeIcon icon={faInfoCircle} />
                            </span>
                          </Tooltip>
                        </label>
                        <input
                          type="number"
                          id="exportRate"
                          value={exportRate}
                          onChange={(e) => setExportRate(e.target.value)}
                          required
                          min="0"
                          step="any"
                        />
                      </div>

                      {/* New input for Percentage Exported */}
                      <div className="form-group">
                        <label htmlFor="percentageExported">
                          Percentage of Electricity Exported (%):
                          <Tooltip content={tooltips.percentageExported}>
                            <span className="info-icon">
                              <FontAwesomeIcon icon={faInfoCircle} />
                            </span>
                          </Tooltip>
                        </label>
                        <input
                          type="number"
                          id="percentageExported"
                          value={percentageExported}
                          onChange={(e) => setPercentageExported(e.target.value)}
                          required
                          min="0"
                          max="100"
                          step="any"
                        />
                      </div>
                    </>
                  )}
                </div>
              </div>

              <button type="submit" className="submit-button">Calculate</button>
            </form>

            {dailyUsage && (
              <div className="result-buttons">
                <button onClick={() => setShowResults(true)} className="result-button show-results-button">
                  Show Results
                </button>
                <button onClick={() => setShowExplanation(true)} className="result-button show-explanation-button">
                  How We Calculate
                </button>
              </div>
            )}
          </div>

          <div className="info-column">
            <h2>Discover the Benefits of Solar Energy</h2>
            <h3>Why Consider Solar Panels?</h3>
            <ol>
              <li>
                <strong>Reduce Energy Bills</strong>
                <p>Solar panels can significantly cut your monthly electricity costs by utilizing free, renewable solar energy. High-efficiency solar panels maximize power generation, leading to substantial savings for households and businesses.</p>
              </li>
              <li>
                <strong>Eco-Friendly Energy</strong>
                <p>Solar energy helps reduce carbon footprints, contributing to the fight against climate change and preserving the environment for future generations. Embrace clean, sustainable power for a positive impact.</p>
              </li>
              <li>
                <strong>Energy Independence</strong>
                <p>By generating your own electricity, you can reduce reliance on traditional energy sources and gain greater control over your energy supply.</p>
              </li>
              <li>
                <strong>Increase Property Value</strong>
                <p>Homes and businesses with solar panel installations often see an increase in property value, making solar energy a smart investment for the future.</p>
              </li>
            </ol>
            <h3>Join the Solar Movement</h3>
            <p>Many have already experienced the transformative benefits of solar energy. Whether aiming to reduce energy bills, embrace sustainability, or enhance property value, solar solutions can meet diverse needs.</p>
            <h3>Explore Solar Energy Today!</h3>
            <p>Consider making the switch to solar energy. Discover how solar power can help achieve energy independence and substantial savings.</p>

            <a 
              href="https://www.sunstore.co.uk/?aff=SolarWealthPlanner" 
              target="_blank" 
              rel="noopener noreferrer"
              style={{
                textDecoration: 'none'
              }}
            >
              <button 
                style={{
                  backgroundColor: '#FFA500',
                  color: '#000000',
                  padding: '15px 30px',
                  fontSize: '1.2rem',
                  fontWeight: 'bold',
                  border: 'none',
                  borderRadius: '5px',
                  cursor: 'pointer',
                  display: 'block',
                  width: '100%',
                  marginTop: '20px',
                  marginBottom: '20px',
                  transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out'
                }}
                onMouseEnter={(e) => {
                  e.target.style.transform = 'translateY(-3px)';
                  e.target.style.boxShadow = '0 4px 8px rgba(0, 0, 0, 0.2)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = 'none';
                }}
              >
                Consult sunstore.co.uk
              </button>
            </a>
            <i>
              *sponsored link
            </i>
          </div>
        </div>
      </div>
      
      {showResults && (
        <ResultsPage
          results={{
            dailyUsage,
            systemSize,
            numberOfPanels,
            totalPanelCost,
            totalInstallationCost,
            totalCost,
            netCost,
            adjustedEfficiency,
            powerReceived,
            currentAnnualCost,
            annualSolarProduction,
            annualSavings,
            exportProfit,
            paybackTime
          }}
          onClose={() => setShowResults(false)}
          onShowExplanation={() => setShowExplanation(true)}
        />
      )}
      
      {showExplanation && (
        <CalculationExplanation onClose={() => setShowExplanation(false)} />
      )}
    </>
  );
};

export default SolarCalculator;
